import { useCallback, useEffect, useState } from "react";
import CHANGE_ACTIVITY_KEYS from "~/constants/activityKeys";
import { captureEvent } from "~/helpers/capture_posthog_event";
import {
  ALERT_AUTO_DISMISS_MS,
  canUserPinActivity,
  PIN_LIMIT,
} from "~/helpers/pinHelpers";
import APIRequest from "~/lib/api_request";

const usePinning = (
  leadId,
  currentUser,
  {
    pinnedActivities,
    setPinnedActivities,
    pinnedCount,
    setPinnedCount,
    setActivities,
  },
) => {
  const [pinAlert, setPinAlert] = useState(null);
  const [pinLoadingIds, setPinLoadingIds] = useState([]);

  // Auto-dismiss pin alerts
  useEffect(() => {
    if (!pinAlert) {
      return;
    }
    const timer = setTimeout(() => setPinAlert(null), ALERT_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [pinAlert]);

  const showPinAlert = useCallback((variant, message) => {
    setPinAlert({ variant, message });
  }, []);

  const handlePinActivity = useCallback(
    (activity) => {
      if (pinnedCount >= PIN_LIMIT) {
        showPinAlert(
          "warning",
          `You can pin up to ${PIN_LIMIT} activities per contact. Unpin an existing activity first.`,
        );
        return;
      }

      const optimisticActivity = {
        ...activity,
        pinned_at: new Date().toISOString(),
      };
      setPinnedActivities((prev) => [optimisticActivity, ...prev]);
      setPinnedCount((c) => c + 1);
      setActivities((prev) => prev.filter((a) => a.id !== activity.id));
      setPinLoadingIds((prev) => [...prev, activity.id]);

      APIRequest.patch({
        resource: `/v1/leads/${leadId}/activities/${activity.id}/pin`,
      }).end((err, res) => {
        setPinLoadingIds((prev) => prev.filter((id) => id !== activity.id));

        if (err || !res?.ok) {
          setPinnedActivities((prev) =>
            prev.filter((a) => a.id !== activity.id),
          );
          setPinnedCount((c) => Math.max(0, c - 1));
          setActivities((prev) => [...prev, activity]);

          if (res?.body?.error === "pin_limit_exceeded") {
            setPinnedCount(res.body.pinned_count);
            showPinAlert(
              "warning",
              `You can pin up to ${PIN_LIMIT} activities per contact. Unpin an existing activity first.`,
            );
          } else {
            showPinAlert(
              "danger",
              "Failed to update pin status. Please try again.",
            );
          }
          return;
        }

        const serverActivity = res.body.activity;
        const serverPinnedCount = res.body.pinned_count;

        if (typeof serverActivity === "string") {
          try {
            const parsed = JSON.parse(serverActivity);
            setPinnedActivities((prev) =>
              prev.map((a) => (a.id === activity.id ? parsed : a)),
            );
          } catch {
            // Keep optimistic version on parse failure
          }
        } else if (serverActivity) {
          setPinnedActivities((prev) =>
            prev.map((a) => (a.id === activity.id ? serverActivity : a)),
          );
        }
        setPinnedCount(serverPinnedCount);

        captureEvent("pinned_note_toggled", {
          action: "pin",
          pinned_count: serverPinnedCount,
          lead_id: leadId,
          activity_type: activity.key,
        });
      });
    },
    [
      leadId,
      pinnedCount,
      setPinnedActivities,
      setPinnedCount,
      setActivities,
      showPinAlert,
    ],
  );

  const handleUnpinActivity = useCallback(
    (activity) => {
      const restoredActivity = { ...activity, pinned_at: null };
      setPinnedActivities((prev) => prev.filter((a) => a.id !== activity.id));
      setPinnedCount((c) => Math.max(0, c - 1));
      setActivities((prev) => [...prev, restoredActivity]);
      setPinLoadingIds((prev) => [...prev, activity.id]);

      APIRequest.patch({
        resource: `/v1/leads/${leadId}/activities/${activity.id}/unpin`,
      }).end((err, res) => {
        setPinLoadingIds((prev) => prev.filter((id) => id !== activity.id));

        if (err || !res?.ok) {
          setPinnedActivities((prev) => [...prev, activity]);
          setPinnedCount((c) => c + 1);
          setActivities((prev) => prev.filter((a) => a.id !== activity.id));
          showPinAlert(
            "danger",
            "Failed to update pin status. Please try again.",
          );
          return;
        }

        const serverPinnedCount = res.body.pinned_count;
        setPinnedCount(serverPinnedCount);

        captureEvent("pinned_note_toggled", {
          action: "unpin",
          pinned_count: serverPinnedCount,
          lead_id: leadId,
          activity_type: activity.key,
        });
      });
    },
    [leadId, setPinnedActivities, setPinnedCount, setActivities, showPinAlert],
  );

  const canUserPin = useCallback(
    () => canUserPinActivity(currentUser),
    [currentUser],
  );

  const buildPinProps = useCallback(
    (activity) => {
      if (CHANGE_ACTIVITY_KEYS.includes(activity.key)) {
        return {};
      }

      const isPinned = !!activity.pinned_at;
      const canPin = canUserPin();

      return {
        isPinned,
        isAtLimit: pinnedCount >= PIN_LIMIT,
        pinnedCount,
        onPinToggle: isPinned ? handleUnpinActivity : handlePinActivity,
        canPin,
        canUnpin: canPin,
        isPinLoading: pinLoadingIds.includes(activity.id),
      };
    },
    [
      pinnedCount,
      pinLoadingIds,
      canUserPin,
      handlePinActivity,
      handleUnpinActivity,
    ],
  );

  return {
    pinAlert,
    setPinAlert,
    pinLoadingIds,
    handlePinActivity,
    handleUnpinActivity,
    canUserPin,
    buildPinProps,
  };
};

export default usePinning;
